import type { ReactNode } from 'react';
import { Download, Share, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { getMobilePlatform, type MobilePlatform } from '@/lib/pwa-install';
import { cn } from '@/lib/utils';

function IosSteps({ className }: { className?: string }) {
  return (
    <ol className={cn('list-decimal list-inside space-y-3 text-sm text-muted-foreground', className)}>
      <li>
        Abre FOCO en <strong className="text-foreground">Safari</strong> (recomendado en iPhone).
      </li>
      <li>
        Toca <Share className="inline h-4 w-4 align-text-bottom mx-0.5" aria-hidden />{' '}
        <strong className="text-foreground">Compartir</strong> en la barra inferior.
      </li>
      <li>
        Elige <strong className="text-foreground">Añadir a pantalla de inicio</strong>.
      </li>
      <li>
        Confirma con <strong className="text-foreground">Añadir</strong>. Verás el icono de FOCO en tu pantalla.
      </li>
    </ol>
  );
}

function AndroidSteps({ className }: { className?: string }) {
  return (
    <ol className={cn('list-decimal list-inside space-y-3 text-sm text-muted-foreground', className)}>
      <li>
        Abre FOCO en <strong className="text-foreground">Chrome</strong> u otro navegador compatible.
      </li>
      <li>
        Toca el menú <strong className="text-foreground">⋮</strong> (arriba a la derecha).
      </li>
      <li>
        Elige <strong className="text-foreground">Instalar aplicación</strong> o{' '}
        <strong className="text-foreground">Añadir a pantalla de inicio</strong>.
      </li>
      <li>Confirma. FOCO quedará como una app en tu teléfono.</li>
    </ol>
  );
}

function PlatformSection({
  platform,
  title,
  children,
  highlighted,
}: {
  platform: MobilePlatform;
  title: string;
  children: ReactNode;
  highlighted: boolean;
}) {
  return (
    <section
      className={cn(
        'rounded-xl border p-4 sm:p-5 space-y-3',
        highlighted ? 'border-primary/40 bg-primary/5' : 'bg-muted/30',
      )}
      aria-labelledby={`pwa-guide-${platform}`}
    >
      <h2 id={`pwa-guide-${platform}`} className="font-semibold text-base flex items-center gap-2">
        <Smartphone className="h-4 w-4 text-primary shrink-0" />
        {title}
        {highlighted && (
          <span className="text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            Tu dispositivo
          </span>
        )}
      </h2>
      {children}
    </section>
  );
}

interface PwaInstallGuideProps {
  showInstallButton?: boolean;
  compact?: boolean;
}

export function PwaInstallGuide({ showInstallButton = true, compact = false }: PwaInstallGuideProps) {
  const platform = getMobilePlatform();
  const { canNativeInstall, installing, promptInstall } = usePwaInstall();
  const isIos = platform === 'ios';
  const isAndroid = platform === 'android';

  return (
    <div className={cn('space-y-4', compact && 'space-y-3')}>
      {!compact && (
        <p className="text-sm text-muted-foreground">
          Instala FOCO como una app en tu pantalla de inicio para abrirla más rápido, sin buscar en el navegador
          cada vez.
        </p>
      )}

      {isAndroid && (
        <PlatformSection platform="android" title="Android" highlighted>
          {canNativeInstall && showInstallButton ? (
            <>
              <p className="text-sm text-muted-foreground">
                Tu navegador permite instalar con un solo toque:
              </p>
              <Button
                type="button"
                className="gap-2"
                onClick={() => promptInstall()}
                disabled={installing}
              >
                <Download className="h-4 w-4" />
                {installing ? 'Instalando…' : 'Instalar FOCO'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Si no aparece el diálogo, sigue los pasos manuales abajo.
              </p>
            </>
          ) : null}
          <AndroidSteps />
        </PlatformSection>
      )}

      {isIos && (
        <PlatformSection platform="ios" title="iPhone / iPad" highlighted>
          <IosSteps />
          <p className="text-xs text-muted-foreground">
            Apple no permite un botón de instalación automática; los pasos anteriores son la forma oficial.
          </p>
        </PlatformSection>
      )}

      {!isIos && !isAndroid && (
        <>
          <PlatformSection platform="android" title="Android" highlighted={false}>
            <AndroidSteps />
          </PlatformSection>
          <PlatformSection platform="ios" title="iPhone / iPad" highlighted={false}>
            <IosSteps />
          </PlatformSection>
        </>
      )}

      {isAndroid && !compact && (
        <PlatformSection platform="ios" title="iPhone / iPad" highlighted={false}>
          <IosSteps />
        </PlatformSection>
      )}

      {isIos && !compact && (
        <PlatformSection platform="android" title="Android" highlighted={false}>
          <AndroidSteps />
        </PlatformSection>
      )}
    </div>
  );
}
